// ** React Imports
import { useCallback, useEffect, useState } from "react";
import { connect, useDispatch } from "react-redux";
import { firestoreConnect } from "react-redux-firebase";
import { compose } from "redux";
import { Button, Radio, Grid, Dropdown, Icon } from "semantic-ui-react";
import CustmerAddress from "../../components/layout/CustmerAddress";
import PlanCard from "../../components/layout/PlanCard";
import { getPaymentDetails } from "../../store/actions/newSignup";
import validator from "validator";
import {
  roundToTwo,
  bankAccountOptions,
  currentYear,
  currentMonth,
} from "../../utils/utils";
import "./Payment.css";
import { debounce } from "lodash";

const Step5 = ({ stepper, selectedPlan, userUid, added, addError, adding }) => {
  const [showBanking, setShowBanking] = useState(false);
  const [showCredit, setShowCredit] = useState(true);
  const [firstOfMonth, setFirstOfMonth] = useState(false);
  const [error, setError] = useState(null);

  // States For Card
  const [holderName, setHolderName] = useState(null);
  const [cardNumber, setCardNumber] = useState(null);
  const [month, setMonth] = useState(null);
  const [year, setYear] = useState(null);
  const [cvv, setCvv] = useState(null);
  const [showCardNumber, setShowCardNumber] = useState(false);
  const [showCVV, setShowCVV] = useState(false);

  // States For Net Banking
  const [actype, setActype] = useState(null);
  const [routingNumber, setRoutingNumber] = useState(null);
  const [acNumber, setAcNumber] = useState(null);
  const [accountHolderName, setAccountHolderName] = useState(null);
  const [showACNumber, setShowACNumber] = useState(false);

  // customer_address
  const [customerAddress, setCustomerAddress] = useState({});
  const [customerAddressEr, setCustomerAddressEr] = useState(null);
  const dispatch = useDispatch();

  const handleOnChange = (event) => {
    const name = event.target.name;
    const value = event.target.value;

    switch (name) {
      case "cardHolderName":
        setHolderName(value);
        setError(null);
        break;
      case "expireMonth":
        if (isNaN(value) || Number(value) > 12 || Number(value) < 0) {
          setError("It should be months in numbers");
          setMonth(null);
        } else {
          setMonth(Number(value));
          setError(null);
        }
        break;
      case "cardNumber":
        if (
          !validator.isCreditCard(value) ||
          (!isNaN(value) && value.length < 12 && value.length > 19)
        ) {
          setError("Please enter a valid card number.");
          setCardNumber(null);
        } else {
          setCardNumber(Number(value));
          setError(null);
        }
        break;
      case "expireYear":
        if (isNaN(value) || Number(value) < currentYear || value.length > 4) {
          setError("Invalid year");
          setYear(null);
        } else {
          setYear(Number(value));
          setError(null);
        }
        break;
      case "cvv":
        if (isNaN(value) || value.length !== 3) {
          setError("Length should be 3");
          setCvv(null);
        } else {
          setCvv(Number(value));
          setError(null);
        }
        break;
      case "routingNumber":
        if (isNaN(value) || value.length !== 9) {
          setError("Length should be 9");
          setRoutingNumber(null);
        } else {
          setRoutingNumber(Number(value));
          setError(null);
        }
        break;
      case "accountNumber":
        if (!value) {
          setError("Fields with * are required");
          setAcNumber(null);
        } else {
          setAcNumber(value);
          setError(null);
        }
        break;
      case "accountHolderName":
        setAccountHolderName(value);
        setError(null);
        break;
      default: {
      }
    }
  };

  const debouncedChangeHandler = useCallback(debounce(handleOnChange, 300), []);

  useEffect(() => {
    if (selectedPlan) {
      if (Object.keys(customerAddress).length < 1) {
        setCustomerAddressEr("Customer address is required");
      }
      if (Object.keys(customerAddress).length === 6) {
        setCustomerAddressEr(null);
      }
    }
  }, [customerAddress, selectedPlan]);

  const handleCredit = () => {
    if (
      holderName &&
      cardNumber &&
      year &&
      cvv &&
      !isNaN(cvv) &&
      !isNaN(cardNumber) &&
      String(cardNumber).length > 15 &&
      String(cardNumber).length < 20 &&
      !isNaN(month) > 0 &&
      !isNaN(month) < 13 &&
      Number(cvv) > 99 &&
      Number(cvv) < 1000
    ) {
      if (3000 >= Number(year) && Number(year) >= currentYear) {
        if (
          Number(year) === Number(currentYear) &&
          Number(month) <= Number(currentMonth)
        ) {
          setError("Your card is expired");
        } else {
          let paymentInformation = {
            paymentMethod: "credit_card",
            card_holder_name: holderName,
            card_number: Number(cardNumber),
            exp_month: Number(month),
            exp_year: Number(year),
            cvv,

            amount: selectedPlan.otp
              ? selectedPlan.amount
              : roundToTwo(selectedPlan.amount / selectedPlan.months),
            months: selectedPlan.otp ? 1 : selectedPlan.months,

            accountHolderName: null,
            accountNumber: null,
            routingNumber: null,
            accountType: null,
          };

          paymentInformation = {
            ...paymentInformation,
            firstOfMonth,
            ...customerAddress,
          };

          dispatch(getPaymentDetails(paymentInformation, userUid));
          setTimeout(() => {}, 2000);
        }
      } else {
        setError("Invalid year");
      }
    } else {
      setError("Invalid input");
    }
  };

  useEffect(() => {
    if (added) {
      setHolderName("");
      setCardNumber("");
      setMonth("");
      setYear("");
      setCvv("");
      setActype("");
      setRoutingNumber("");
      setAcNumber("");
      setAccountHolderName("");
      setCustomerAddress("");
      setCustomerAddressEr("");
      stepper.next();
      window.scroll(0, 0);
    } else if (addError) {
      setError(addError);
    }
  }, [added, addError]);

  const handleBanking = () => {
    if (
      actype &&
      !isNaN(routingNumber) &&
      String(routingNumber).length === 9 &&
      acNumber &&
      accountHolderName
    ) {
      let paymentInformation = {
        paymentMethod: "echeck",
        accountHolderName,
        accountNumber: acNumber,
        routingNumber: Number(routingNumber),
        accountType: actype,

        amount: selectedPlan.otp
          ? selectedPlan.amount
          : roundToTwo(selectedPlan.amount / selectedPlan.months),
        months: selectedPlan.otp ? 1 : selectedPlan.months,

        card_holder_name: null,
        card_number: null,
        exp_month: null,
        exp_year: null,
        cvv: null,
      };
      paymentInformation = {
        ...paymentInformation,
        firstOfMonth,
        ...customerAddress,
      };
      dispatch(getPaymentDetails(paymentInformation, userUid));
    } else {
      setError("Your input data isn't correct");
    }
  };

  return (
    <>
      <Grid divided="vertically" className="payment-main">
        <Grid.Row stackable="true">
          <Grid.Column mobile={16} tablet={10} computer={10}>
            <div className="step-header">
              <h2 className="step-header-head">Payment method</h2>
              <p className="step-header-text">
                Fill the information and make payment
                <span className="required">*</span>
              </p>
              {error && <p className="errorText">{error}</p>}
              {customerAddressEr && (
                <p className="errorText">{customerAddressEr}</p>
              )}
            </div>
            <div className="form-group mb-0">
              {!selectedPlan?.otp && (
                <label className="radio-label">We accept:</label>
              )}
              <div className="choose-data smalls with-line">
                {!selectedPlan?.otp && (
                  <div className="form-group mb-1">
                    {/* <label className="radio-label blank"></label> */}
                    <div className="choose-data smalls with-line">
                      <Radio
                        defaultChecked
                        name="dateOfPayment"
                        id="1stOfTheMonth"
                        label="1st Of Month"
                        checked={firstOfMonth}
                        onChange={() => setFirstOfMonth(true)}
                        className="first"
                      />
                      <Radio
                        name="dateOfPayment"
                        id="halfOfTheMonth"
                        label="15th Of Month"
                        checked={!firstOfMonth}
                        onChange={() => setFirstOfMonth(false)}
                      />
                    </div>
                  </div>
                )}
                {/* <Radio
                  defaultChecked
                  name="credit"
                  id="credit"
                  label="Credit Card"
                  checked={showCredit}
                  onChange={() => {
                    setShowCredit(true);
                    setShowBanking(false);
                    setActype("");
                    setRoutingNumber("");
                    setAcNumber("");
                    setAccountHolderName("");
                  }}
                  className="first"
                />
                <Radio
                  name="netbanking"
                  id="netbanking"
                  label="Net Banking"
                  checked={showBanking}
                  onChange={() => {
                    setShowCredit(false);
                    setShowBanking(true);
                    setHolderName("");
                    setCardNumber("");
                    setMonth("");
                    setYear("");
                    setCvv("");
                  }}
                /> */}
              </div>
            </div>
            {!showBanking && showCredit && (
              <>
                <div className="set-two-data">
                  <div className="one">
                    <div className="form-group ">
                      <label htmlFor="chname">
                        Card Holder Name <span className="required">*</span>
                      </label>
                      <input
                        id="chname"
                        type="text"
                        placeholder="Firstname Lastname Surname"
                        autoComplete="off"
                        name="cardHolderName"
                        onChange={debouncedChangeHandler}
                      />
                    </div>
                  </div>
                  <div className="one">
                    <div className="form-group">
                      <label htmlFor="cnumber">
                        Card Number <span className="required">*</span>
                      </label>
                      <div className="input-eye">
                        <input
                          id="cnumber"
                          type={!showCardNumber ? "password" : "text"}
                          placeholder="4111 1111 1111 1111"
                          autoComplete="off"
                          name="cardNumber"
                          onChange={debouncedChangeHandler}
                        />
                        <Icon
                          className="password-icon"
                          name={`eye ${showCardNumber ? "slash" : ""}`}
                          onClick={() => setShowCardNumber(!showCardNumber)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="set-three-data">
                  <div className="one">
                    <div className="form-group ">
                      <label htmlFor="expmonth">
                        Expiration Month <span className="required">*</span>
                      </label>
                      <input
                        id="expmonth"
                        type="text"
                        placeholder="12"
                        autoComplete="off"
                        name="expireMonth"
                        onChange={debouncedChangeHandler}
                      />
                    </div>
                  </div>
                  <div className="two">
                    <div className="form-group ">
                      <label htmlFor="expyear">
                        Expiration Year <span className="required">*</span>
                      </label>
                      <input
                        id="expyear"
                        type={"text"}
                        placeholder="2024"
                        autoComplete="off"
                        name="expireYear"
                        onChange={debouncedChangeHandler}
                      />
                    </div>
                  </div>
                  <div className="three">
                    <div className="form-group ">
                      <label htmlFor="cvv">
                        CVV <span className="required">*</span>
                      </label>
                      <div className="input-eye">
                        <input
                          id="cvv"
                          type={!showCVV ? "password" : "text"}
                          placeholder="123"
                          autoComplete="off"
                          name="cvv"
                          onChange={debouncedChangeHandler}
                        />
                        <Icon
                          className="password-icon"
                          name={`eye ${showCVV ? "slash" : ""}`}
                          onClick={() => setShowCVV(!showCVV)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
            {showBanking && !showCredit && (
              <>
                <div className="set-two-data">
                  <div className="one">
                    <div className="form-group ">
                      <label htmlFor="atype">
                        Account type <span className="required">*</span>
                      </label>
                      <Dropdown
                        className="cu-form-groups-dropdown"
                        options={bankAccountOptions}
                        fluid
                        selection
                        placeholder="Account Type"
                        onChange={(e, data) => {
                          setActype(data.value);
                        }}
                      />
                    </div>
                  </div>
                  <div className="one">
                    <div className="form-group ">
                      <label htmlFor="rnumber">
                        Routing number <span className="required">*</span>
                      </label>
                      <input
                        id="rnumber"
                        type="text"
                        placeholder="Enter routing number"
                        autoComplete="off"
                        name="routingNumber"
                        onChange={debouncedChangeHandler}
                      />
                    </div>
                  </div>
                  <div className="one">
                    <div className="form-group ">
                      <label htmlFor="accnumber">
                        Account number <span className="required">*</span>
                      </label>
                      <div className="input-eye">
                        <input
                          id="accnumber"
                          type={!showACNumber ? "password" : "text"}
                          placeholder="Enter account number"
                          autoComplete="off"
                          name="accountNumber"
                          onChange={debouncedChangeHandler}
                        />
                        <Icon
                          className="password-icon"
                          name={`eye ${showACNumber ? "slash" : ""}`}
                          onClick={() => setShowACNumber(!showACNumber)}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="one">
                    <div className="form-group ">
                      <label htmlFor="accname">
                        Account Holder name <span className="required">*</span>
                      </label>
                      <input
                        id="accname"
                        type="text"
                        placeholder="Enter account holder number"
                        autoComplete="off"
                        name="accountHolderName"
                        onChange={debouncedChangeHandler}
                      />
                    </div>
                  </div>
                </div>
              </>
            )}
            <CustmerAddress
              getCustomerAddress={(e) => setCustomerAddress(e)}
              clearFields={added ? true : false}
            />
            <p className="not-storing">
              <i>
                We are not storing any Card/Bank related details in our portal
              </i>
            </p>
          </Grid.Column>
          <Grid.Column mobile={16} tablet={6} computer={6}>
            <div className="step-header">
              <h2 className="step-header-head cu-center">
                Subscription detail
              </h2>
            </div>
            {selectedPlan && (
              <div className="step-data">
                <div className="step-data-body">
                  <div className="new-student-package single">
                    <PlanCard plan={selectedPlan} hide />
                  </div>
                </div>
              </div>
            )}
            <div className="footer-btn-data">
              <Button
                onClick={() => {
                  setHolderName("");
                  setCardNumber("");
                  setMonth("");
                  setYear("");
                  setCvv("");
                  setActype("");
                  setRoutingNumber("");
                  setAcNumber("");
                  setAccountHolderName("");
                  setCustomerAddress("");
                  setCustomerAddressEr("");

                  stepper.previous();
                  window.scroll(0, 0);
                }}
              >
                Back
              </Button>
              <Button
                primary
                disabled={error || customerAddressEr}
                loading={adding}
                onClick={() => {
                  showCredit ? handleCredit() : handleBanking();
                }}
              >
                Next
              </Button>
            </div>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    added: state.signup.paymentDetailsAdd,
    adding: state.signup.paymentDetailsAdding,
    addError: state.signup.paymentDetailsAddError,
  };
};
export default compose(
  connect(mapStateToProps),
  firestoreConnect([{ collection: "testPriceList" }])
)(Step5);
